import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contentService } from '@/services/content.service';
import { coursesService } from '@/services/courses.service';
import { Upload, FileText, Video, CheckCircle, Clock, AlertCircle, Trash2, History, Plus, Eye, GraduationCap, BookOpen, Code2, Download } from 'lucide-react';
import { UploadWizard } from '@/components/UploadWizard';
import { ViewToggle } from '@/components/ViewToggle';
import { PaginationControls } from '@/components/PaginationControls';
import { useToast } from '@/contexts/ToastContext';
import { confirmDelete, showDeleteSuccess, showDeleteError } from '@/utils/deleteConfirmation';
import { PageHeader } from '@/components/PageHeader';
import { API_BASE_URL } from '@/config/api.config';
import type { LearningResource } from '@/types';

export function MaterialsList() {
  const { showToast } = useToast();
  const [showUploadWizard, setShowUploadWizard] = useState(false);
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [paginationMode, setPaginationMode] = useState<'pagination' | 'infinite'>('pagination');
  const [currentPage, setCurrentPage] = useState(1);
  const [displayCount, setDisplayCount] = useState(15);
  const [courseFilter, setCourseFilter] = useState<string>('all');
  const [viewMaterial, setViewMaterial] = useState<LearningResource | null>(null);
  const itemsPerPage = 15;
  const queryClient = useQueryClient();

  // Fetch courses for filter dropdown
  const { data: coursesData } = useQuery({
    queryKey: ['courses'],
    queryFn: async () => {
      try {
        const courses = await coursesService.getCourses();
        return Array.isArray(courses) ? courses : [];
      } catch (error) {
        console.error('[MaterialsList] Failed to fetch courses:', error);
        return [];
      }
    },
  });

  const { data: allMaterials, isLoading } = useQuery({
    queryKey: ['materials', courseFilter],
    queryFn: async () => {
      try {
        const token = localStorage.getItem('accessToken') || '';
        const filters: any = {};

        // Add course filter if selected
        if (courseFilter !== 'all') {
          filters.course = courseFilter;
        }

        const response = await contentService.getDocuments(filters, token);
        return response.documents || [];
      } catch (error) {
        console.error('Failed to fetch materials:', error);
        return [];
      }
    },
    // Auto-refresh every 5 seconds if any materials are still processing or extracting topics
    refetchInterval: (query) => {
      const data = query.state.data;
      const hasProcessing = data?.some((material: any) =>
        material.processing_status === 'processing' ||
        material.processing_status === 'pending' ||
        material.extractionStatus === 'extracting' ||
        material.extractionStatus === 'pending'
      );
      return hasProcessing ? 5000 : false; // Poll every 5 seconds if processing/extracting, otherwise stop
    },
  });

  const totalItems = allMaterials?.length || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Display materials based on mode
  const materials = useMemo(() => {
    if (!allMaterials) return undefined;
    if (paginationMode === 'infinite') {
      return allMaterials.slice(0, displayCount);
    } else {
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      return allMaterials.slice(startIndex, endIndex);
    }
  }, [allMaterials, paginationMode, displayCount, currentPage, itemsPerPage]);

  const handleLoadMore = () => {
    setDisplayCount(prev => prev + itemsPerPage);
  };

  const handlePaginationModeChange = (mode: 'pagination' | 'infinite') => {
    setPaginationMode(mode);
    if (mode === 'pagination') {
      setCurrentPage(1);
    } else {
      setDisplayCount(itemsPerPage);
    }
  };

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const token = localStorage.getItem('accessToken') || '';
      return await contentService.deleteDocument(id, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['materials'] });
      showDeleteSuccess('The material has been deleted successfully.');
    },
    onError: () => {
      showDeleteError('Failed to delete the material. Please try again.');
    },
  });

  const deleteAllMutation = useMutation({
    mutationFn: async () => {
      const token = localStorage.getItem('accessToken') || '';
      return await contentService.deleteAllDocuments(token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['materials'] });
      showDeleteSuccess('All learning materials have been successfully deleted.');
    },
    onError: (error: any) => {
      showDeleteError(error.response?.data?.error || 'Failed to delete all materials');
    },
  });

  const handleDeleteClick = async (material: LearningResource) => {
    const confirmed = await confirmDelete({
      title: 'Delete Material?',
      itemName: material.title,
    });

    if (confirmed) {
      deleteMutation.mutate(material.resource_id);
    }
  };

  const handleDeleteAll = async () => {
    const totalMaterials = materials?.length || 0;
    const confirmed = await confirmDelete({
      title: 'Delete ALL Learning Materials?',
      itemName: `${totalMaterials} materials`,
    });

    if (confirmed) {
      deleteAllMutation.mutate();
    }
  };

  const handleView = (material: LearningResource) => {
    setViewMaterial(material);
  };

  const handleDownload = async (material: LearningResource) => {
    try {
      const token = localStorage.getItem('accessToken') || '';

      // Create download URL
      const downloadUrl = `${API_BASE_URL}/content/${material.resource_id}/download`;

      // Fetch the file
      const response = await fetch(downloadUrl, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.error || 'Download failed';

        if (response.status === 404) {
          throw new Error('File not available for download. This file was uploaded before the download feature was implemented.');
        }
        throw new Error(errorMsg);
      }

      // Get the blob
      const blob = await response.blob();

      // Create download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = material.title || 'document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showToast('success', 'Download Started', `Downloading ${material.title}`);
    } catch (error: any) {
      console.error('Download error:', error);
      showToast('error', 'Download Failed', error.message || 'Failed to download file');
    }
  };

  const getOECodesFromMaterial = (material: any) => {
    // Only show explicit OE codes (detected directly from text)
    if (material?.aiDetectionResults?.oeCodes) {
      return material.aiDetectionResults.oeCodes
        .filter((code: any) => code.method === 'ai-explicit')
        .map((code: any) => ({
          oeCode: code.oeCode,
          confidence: code.confidence,
          method: code.method,
          context: code.context,
          heading: code.heading,
          lineNumber: code.lineNumber,
          isBold: code.isBold,
          isExplicit: true
        }));
    }

    return [];
  };

  // Get OE codes from the viewed material using the same logic as listing
  const oeCodes = viewMaterial ? getOECodesFromMaterial(viewMaterial) : [];

  const getFileIcon = (fileType?: string) => {
    if (fileType && fileType.includes('video')) return Video;
    return FileText;
  };

  const getStatusIcon = (status: LearningResource['processing_status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'processing':
        return <Clock className="w-5 h-5 text-blue-600 animate-spin" />;
      case 'failed':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status: LearningResource['processing_status']) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getFormattedFileType = (fileType?: string) => {
    if (!fileType) return 'Unknown';

    // Handle common MIME types
    const mimeTypeMap: Record<string, string> = {
      'application/pdf': 'PDF',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint (PPTX)',
      'application/vnd.ms-powerpoint': 'PowerPoint (PPT)',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word (DOCX)',
      'application/msword': 'Word (DOC)',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel (XLSX)',
      'application/vnd.ms-excel': 'Excel (XLS)',
      'text/plain': 'Text File',
      'video/mp4': 'Video (MP4)',
      'video/mpeg': 'Video (MPEG)',
    };

    return mimeTypeMap[fileType] || fileType.split('/')[1]?.toUpperCase() || 'File';
  };

  // Filter only active materials
  const activeMaterials = materials?.filter(m => m.is_active !== false) || [];
  const deprecatedMaterials = materials?.filter(m => m.is_active === false) || [];

  return (
    <div className="space-y-6">
      {/* Page Header with Breadcrumbs */}
      <div className="flex items-start justify-between">
        <PageHeader
          title="Learning Materials"
          description="Upload and manage materials for RAG-powered question generation"
          breadcrumbs={[{ label: 'Content Management', path: '#' }, { label: 'Learning Materials' }]}
        />
        <div className="flex items-center space-x-3 mt-10">
          {materials && materials.length > 0 && (
            <button
              onClick={handleDeleteAll}
              disabled={deleteAllMutation.isPending}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {deleteAllMutation.isPending ? 'Deleting...' : 'Delete All'}
            </button>
          )}
          <button
            onClick={() => setShowUploadWizard(true)}
            className="btn-primary flex items-center"
          >
            <Plus className="w-5 h-5 mr-2" />
            Upload Materials
          </button>
        </div>
      </div>

      {/* View Controls and Filters */}
      <div className="card">
        <div className="flex items-center justify-between gap-4">
          {/* Course Filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Filter by Course:</label>
            <select
              value={courseFilter}
              onChange={(e) => {
                setCourseFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="input-field w-64"
            >
              <option value="all">All Courses</option>
              {(coursesData || []).map((course: any) => (
                <option key={course.course_id} value={course.course_id}>
                  {course.name}
                </option>
              ))}
            </select>
          </div>

          <ViewToggle view={view} onViewChange={setView} />
        </div>
      </div>

      {/* Stats Cards */}
      {materials && materials.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Materials</p>
                <p className="text-2xl font-bold text-gray-900">{materials.length}</p>
              </div>
              <FileText className="w-10 h-10 text-gray-400" />
            </div>
          </div>
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Active</p>
                <p className="text-2xl font-bold text-green-600">{activeMaterials.length}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
          </div>
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm text-gray-600">Processing</p>
                  {(materials.filter(m => m.processing_status === 'processing' || (m as any).extractionStatus === 'extracting').length > 0) && (
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full animate-pulse">
                      Auto-refreshing
                    </span>
                  )}
                </div>
                <p className="text-2xl font-bold text-blue-600">
                  {materials.filter(m => m.processing_status === 'processing' || (m as any).extractionStatus === 'extracting').length}
                </p>
              </div>
              <Clock className="w-10 h-10 text-blue-400" />
            </div>
          </div>
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Pages</p>
                <p className="text-2xl font-bold text-primary-600">
                  {activeMaterials.reduce((sum, m) => sum + (m.pages || 0), 0).toLocaleString()}
                </p>
              </div>
              <Upload className="w-10 h-10 text-primary-400" />
            </div>
          </div>
        </div>
      )}

      {/* Materials List */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Active Materials</h2>
          {deprecatedMaterials.length > 0 && (
            <button className="text-sm text-gray-600 hover:text-gray-900 flex items-center">
              <History className="w-4 h-4 mr-1" />
              {deprecatedMaterials.length} deprecated version(s)
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="py-12 text-center text-gray-500">Loading...</div>
        ) : !materials || materials.length === 0 ? (
          <div className="py-12 text-center">
            <Upload className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium text-gray-900 mb-2">No materials uploaded yet</p>
            <p className="text-sm text-gray-500 mb-4">
              Upload learning materials to power RAG-based question generation
            </p>
            <button
              onClick={() => setShowUploadWizard(true)}
              className="btn-primary inline-flex items-center"
            >
              <Plus className="w-5 h-5 mr-2" />
              Upload First Material
            </button>
          </div>
        ) : view === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {activeMaterials.map((material) => {
              const FileIcon = getFileIcon(material.file_type);

              return (
                <div
                  key={material.resource_id}
                  className="card hover:shadow-lg transition-shadow border-l-4 border-primary-500"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <div className="p-3 bg-primary-50 rounded-lg flex-shrink-0">
                        <FileIcon className="w-6 h-6 text-primary-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3
                          className="text-sm font-semibold text-gray-900 truncate"
                          title={material.title}
                        >
                          {material.title}
                        </h3>
                        <div className="flex items-center space-x-2 mt-1">
                          {material.version && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                              v{material.version}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div className="mb-3">
                    <span
                      className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${getStatusColor(
                        material.processing_status
                      )}`}
                    >
                      <span className="mr-1.5">{getStatusIcon(material.processing_status)}</span>
                      {material.processing_status}
                    </span>
                  </div>

                  {/* Details */}
                  <div className="space-y-2 mb-4 text-sm">
                    {material.pages && material.pages > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Pages:</span>
                        <span className="font-medium text-primary-600">
                          {material.pages.toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* OE Codes - All Visible */}
                  {(() => {
                    const codes = getOECodesFromMaterial(material);
                    return (
                      <div className="pt-3 border-t border-gray-100">
                        <div className="flex items-center space-x-2 mb-3">
                          <Code2 className="w-4 h-4 text-blue-600" />
                          <span className="text-gray-700 font-bold text-xs">OE CODES ({codes.length})</span>
                        </div>
                        <div className="max-h-48 overflow-y-auto bg-blue-50 p-3 rounded-lg border border-blue-100">
                          <div className="flex flex-wrap gap-2">
                            {codes.length > 0 ? (
                              codes.map((code: any, idx: number) => (
                                <div key={idx} className="bg-white border border-blue-200 rounded px-2.5 py-1.5 group hover:shadow-md transition-shadow">
                                  <span className="text-xs font-bold text-blue-700">
                                    {code.oeCode}
                                  </span>
                                  {code.confidence && (
                                    <div className="text-xs text-gray-500 mt-0.5">
                                      {(code.confidence * 100).toFixed(0)}%
                                    </div>
                                  )}
                                </div>
                              ))
                            ) : (
                              <div className="bg-white border border-gray-300 rounded px-2.5 py-1.5">
                                <span className="text-xs font-bold text-gray-500">
                                  N/A
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Footer Info */}
                  <div className="pt-3 border-t border-gray-200">
                    <div className="text-xs text-gray-500 mb-3">
                      <div>📅 {new Date(material.created_at || '').toLocaleDateString()}</div>
                      <div className="mt-1">📚 {material.source_authority}</div>
                      <div className="mt-1">🏷️ {material.resource_type}</div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleView(material as any)}
                        className="flex-1 btn-secondary text-sm py-2 flex items-center justify-center"
                        title="View details"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </button>
                      {/* {(material as any).storedFilename && (
                        <button
                          onClick={() => handleDownload(material as any)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Download"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      )} */}
                      <button
                        onClick={() => handleDeleteClick(material as any)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b-2 border-gray-300 bg-gray-50">
                  <th className="pb-4 px-4 text-sm font-semibold text-gray-700">Title</th>
                  <th className="pb-4 px-4 text-sm font-semibold text-gray-700">Status</th>
                  <th className="pb-4 px-4 text-sm font-semibold text-gray-700">Pages</th>
                  <th className="pb-4 px-4 text-sm font-semibold text-gray-700">OE Codes</th>
                  <th className="pb-4 px-4 text-sm font-semibold text-gray-700">Created</th>
                  <th className="pb-4 px-4 text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {activeMaterials.map((material, idx) => {
                  const FileIcon = getFileIcon(material.file_type);
                  return (
                    <tr key={material.resource_id} className={`border-b border-gray-200 hover:bg-purple-50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <td className="py-4 px-4">
                        <div className="flex items-center space-x-3 max-w-md">
                          <div className="p-2 bg-primary-50 rounded flex-shrink-0">
                            <FileIcon className="w-4 h-4 text-primary-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p
                              className="font-medium text-gray-900 text-sm truncate"
                              title={material.title}
                            >
                              {material.title}
                            </p>
                            {material.version && (
                              <span className="text-xs text-gray-500">v{material.version}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span
                          className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                            material.processing_status
                          )}`}
                        >
                          {getStatusIcon(material.processing_status)}
                          <span className="ml-1">{material.processing_status}</span>
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm font-medium text-primary-600">
                          {material.pages?.toLocaleString() || 0}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        {(() => {
                          const codes = getOECodesFromMaterial(material);
                          return codes.length > 0 ? (
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 rounded-md border border-blue-200">
                                <Code2 className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                                <span className="text-xs font-bold text-blue-700">
                                  {codes.length}
                                </span>
                              </div>
                              <div className="flex items-center gap-1.5 overflow-x-auto max-w-md">
                                {codes.slice(0, 4).map((code: any, idx: number) => (
                                  <span
                                    key={idx}
                                    className="inline-flex items-center text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-medium whitespace-nowrap border border-blue-300"
                                    title={code.heading || code.oeCode}
                                  >
                                    {code.oeCode}
                                  </span>
                                ))}
                                {codes.length > 4 && (
                                  <span className="inline-flex items-center text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-medium border border-gray-300 whitespace-nowrap">
                                    +{codes.length - 4}
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="inline-flex items-center text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded font-bold border border-gray-300">
                              N/A
                            </span>
                          );
                        })()}
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm text-gray-600">
                          {new Date(material.created_at || '').toLocaleDateString()}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleView(material as any)}
                            className="p-2 text-gray-600 transition-colors hover:text-primary-600 hover:bg-gray-100 rounded"
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {(material as any).storedFilename && (
                            <button
                              onClick={() => handleDownload(material as any)}
                              className="p-2 text-gray-600 transition-colors hover:text-blue-600 hover:bg-gray-100 rounded"
                              title="Download"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteClick(material as any)}
                            className="p-2 text-gray-600 transition-colors hover:text-red-600 hover:bg-gray-100 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {totalItems > 0 && (
        <PaginationControls
          mode={paginationMode}
          onModeChange={handlePaginationModeChange}
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onLoadMore={handleLoadMore}
          hasMore={displayCount < totalItems}
          isLoading={isLoading}
        />
      )}

      {/* Upload Wizard Modal */}
      {showUploadWizard && (
        <UploadWizard
          onClose={() => setShowUploadWizard(false)}
          onUploadComplete={() => {
            queryClient.invalidateQueries({ queryKey: ['materials'] });
            setCurrentPage(1);
          }}
        />
      )}

      {/* View Material Details Modal */}
      {viewMaterial && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden animate-slideUp">
            {/* Header */}
            <div className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white px-8 py-6">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full -mr-32 -mt-32"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white opacity-5 rounded-full -ml-24 -mb-24"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-white bg-opacity-20 rounded-xl backdrop-blur-sm">
                    <FileText className="w-7 h-7" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Learning Material</h2>
                    <p className="text-primary-100 text-sm mt-0.5">Complete material information</p>
                  </div>
                </div>
                <button
                  onClick={() => setViewMaterial(null)}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-xl p-2.5 transition-all duration-200 hover:rotate-90"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="overflow-y-auto max-h-[calc(90vh-12rem)]">
              <div className="p-8 space-y-6">
                {/* Title & Status */}
                <div className="pb-6 border-b border-gray-100">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-3xl font-bold text-gray-900 mb-3 leading-tight">{viewMaterial.title}</h3>
                      <div className="flex items-center gap-3">
                        {viewMaterial.version && (
                          <span className="inline-flex items-center px-3 py-1.5 text-sm font-semibold bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg shadow-sm">
                            <span className="mr-1">📦</span> Version {viewMaterial.version}
                          </span>
                        )}
                        <span className={`inline-flex items-center px-4 py-1.5 text-sm font-semibold rounded-lg shadow-sm ${getStatusColor(viewMaterial.processing_status)}`}>
                          {getStatusIcon(viewMaterial.processing_status)}
                          <span className="ml-2 capitalize">{viewMaterial.processing_status}</span>
                        </span>
                      </div>
                    </div>
                  </div>

                  {(viewMaterial as any).description && (
                    <p className="text-gray-600 leading-relaxed bg-gray-50 p-4 rounded-lg border-l-4 border-primary-500">{(viewMaterial as any).description}</p>
                  )}
                </div>

              {/* Basic Information */}
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <div className="w-1 h-6 bg-gradient-to-b from-primary-500 to-primary-600 rounded-full mr-3"></div>
                  Quick Overview
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="group relative bg-gradient-to-br from-primary-50 to-primary-100 p-5 rounded-xl border border-primary-200 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl shadow-lg group-hover:scale-110 transition-transform flex-shrink-0">
                        <GraduationCap className="w-6 h-6 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-primary-600 uppercase tracking-wider mb-1">Exam</p>
                        <p className="text-lg font-bold text-gray-900 truncate">{viewMaterial.exam_id}</p>
                      </div>
                    </div>
                  </div>

                  {viewMaterial.course_id && (
                    <div className="group relative bg-gradient-to-br from-blue-50 to-blue-100 p-5 rounded-xl border border-blue-200 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                      <div className="flex items-center space-x-4">
                        <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg group-hover:scale-110 transition-transform flex-shrink-0">
                          <BookOpen className="w-6 h-6 text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-1">Course</p>
                          <p className="text-lg font-bold text-gray-900 truncate">{viewMaterial.course_id}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="group relative bg-gradient-to-br from-orange-50 to-orange-100 p-5 rounded-xl border border-orange-200 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg group-hover:scale-110 transition-transform flex-shrink-0">
                        <FileText className="w-6 h-6 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-orange-600 uppercase tracking-wider mb-1">File Type</p>
                        <p className="text-lg font-bold text-gray-900 truncate" title={viewMaterial.file_type}>
                          {getFormattedFileType(viewMaterial.file_type)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="group relative bg-gradient-to-br from-green-50 to-green-100 p-5 rounded-xl border border-green-200 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                    <div className="flex items-center space-x-4">
                      <div className="p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg group-hover:scale-110 transition-transform flex-shrink-0">
                        <Upload className="w-6 h-6 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-green-600 uppercase tracking-wider mb-1">File Size</p>
                        <p className="text-lg font-bold text-gray-900">{viewMaterial.file_size_mb || 0} MB</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Processing Details */}
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <div className="w-1 h-6 bg-gradient-to-b from-primary-500 to-primary-600 rounded-full mr-3"></div>
                  Processing Details
                </h4>
                <div className="bg-gradient-to-br from-primary-50 to-primary-100 p-6 rounded-xl border border-primary-200">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center w-14 h-14 bg-white rounded-xl shadow-sm mb-3">
                        <FileText className="w-7 h-7 text-primary-600" />
                      </div>
                      <p className="text-xs font-semibold text-primary-700 uppercase tracking-wider mb-1">Total Pages</p>
                      <p className="text-2xl font-bold text-gray-900">{viewMaterial.pages || 0}</p>
                    </div>
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center w-14 h-14 bg-white rounded-xl shadow-sm mb-3">
                        <Upload className="w-7 h-7 text-primary-600" />
                      </div>
                      <p className="text-xs font-semibold text-primary-700 uppercase tracking-wider mb-1">Chunks Created</p>
                      <p className="text-2xl font-bold text-primary-600">{viewMaterial.chunk_count?.toLocaleString() || 0}</p>
                    </div>
                    {viewMaterial.processing_progress !== undefined && (
                      <div className="text-center">
                        <div className="inline-flex items-center justify-center w-14 h-14 bg-white rounded-xl shadow-sm mb-3">
                          <Clock className="w-7 h-7 text-primary-600" />
                        </div>
                        <p className="text-xs font-semibold text-primary-700 uppercase tracking-wider mb-1">Progress</p>
                        <p className="text-2xl font-bold text-gray-900">{viewMaterial.processing_progress}%</p>
                      </div>
                    )}
                  </div>

                  {viewMaterial.error_message && (
                    <div className="mt-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm">
                      <p className="text-sm text-red-800 flex items-start">
                        <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                        <span><span className="font-bold">Error:</span> {viewMaterial.error_message}</span>
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* OE Codes Extracted - Detailed Cards */}
              {oeCodes && oeCodes.length > 0 && (
                <div>
                  <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full mr-3"></div>
                    <Code2 className="w-5 h-5 text-blue-600 mr-2" />
                    Extracted OE Codes
                    <span className="ml-2 text-sm font-semibold text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
                      {oeCodes.length} detected
                    </span>
                  </h4>
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200 max-h-96 overflow-y-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {oeCodes.map((code: any, idx: number) => (
                        <div
                          key={idx}
                          className="bg-white p-4 rounded-lg border-l-4 border-blue-500 shadow-sm hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <span className="inline-flex items-center text-sm bg-blue-600 text-white px-3 py-1 rounded-md font-bold">
                              {code.oeCode}
                            </span>
                            {code.confidence && (
                              <span className="text-xs font-semibold text-blue-700 bg-blue-100 px-2 py-1 rounded">
                                {(code.confidence * 100).toFixed(0)}%
                              </span>
                            )}
                          </div>
                          {code.heading && (
                            <p className="text-xs text-gray-700 mb-1 line-clamp-2" title={code.heading}>
                              {code.heading}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                            {code.lineNumber && (
                              <span className="flex items-center">
                                📄 Line {code.lineNumber}
                              </span>
                            )}
                            {code.isBold && (
                              <span className="flex items-center font-semibold text-blue-600">
                                ✓ Bold
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}


              {/* Metadata */}
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <div className="w-1 h-6 bg-gradient-to-b from-gray-500 to-gray-600 rounded-full mr-3"></div>
                  Additional Information
                </h4>
                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                      <span className="text-sm font-medium text-gray-600">Resource Type</span>
                      <span className="text-sm font-bold text-gray-900">{viewMaterial.resource_type || 'N/A'}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                      <span className="text-sm font-medium text-gray-600">Source Authority</span>
                      <span className="text-sm font-bold text-gray-900">{viewMaterial.source_authority || 'N/A'}</span>
                    </div>
                    {viewMaterial.author && (
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                        <span className="text-sm font-medium text-gray-600">Author</span>
                        <span className="text-sm font-bold text-gray-900">{viewMaterial.author}</span>
                      </div>
                    )}
                    {viewMaterial.language && (
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                        <span className="text-sm font-medium text-gray-600">Language</span>
                        <span className="text-sm font-bold text-gray-900">{viewMaterial.language}</span>
                      </div>
                    )}
                    {viewMaterial.publication_date && (
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg md:col-span-2">
                        <span className="text-sm font-medium text-gray-600">Publication Date</span>
                        <span className="text-sm font-bold text-gray-900">
                          {new Date(viewMaterial.publication_date).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Timestamps */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-6 border-t border-gray-200">
                <div className="flex items-center text-sm text-gray-600 bg-gray-50 px-4 py-2 rounded-lg">
                  <Clock className="w-4 h-4 mr-2 text-primary-600" />
                  <span className="font-medium">Uploaded:</span>
                  <span className="ml-2">{new Date(viewMaterial.created_at || '').toLocaleString()}</span>
                </div>
                {viewMaterial.updated_at && (
                  <div className="flex items-center text-sm text-gray-600 bg-gray-50 px-4 py-2 rounded-lg">
                    <History className="w-4 h-4 mr-2 text-blue-600" />
                    <span className="font-medium">Updated:</span>
                    <span className="ml-2">{new Date(viewMaterial.updated_at).toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>
            </div>

            {/* Footer Actions */}
            <div className="sticky bottom-0 bg-gradient-to-r from-gray-50 to-gray-100 px-8 py-5 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600 flex items-center">
                  <FileText className="w-4 h-4 mr-2 text-gray-400" />
                  Material ID: <span className="font-mono font-semibold ml-1 text-gray-900">{viewMaterial.resource_id?.slice(0, 8)}...</span>
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setViewMaterial(null);
                      handleDeleteClick(viewMaterial);
                    }}
                    className="px-5 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all duration-200 flex items-center font-semibold shadow-sm hover:shadow-md"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </button>
                  <button
                    onClick={() => setViewMaterial(null)}
                    className="px-6 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all duration-200 font-semibold shadow-md hover:shadow-lg"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
